import React, { useEffect, useState } from "react";
import { marketSentiment, voteMarket } from "../../../api/user";
import { toast } from "react-toastify";

const BULL_IMG = `${process.env.PUBLIC_URL}/Bull.png`;
const BEAR_IMG = `${process.env.PUBLIC_URL}/Bearish.png`;

// A feed card that mirrors the /market-sentiment voting flow:
//   - get-market-sentiment  -> { bull, bear, hasVoted, ... }
//   - create-vote ("1"=BULL, "2"=BEAR) -> updated { bull, bear, hasVoted }
// Pre-vote shows the Bullish / Bearish buttons; once the user votes (or has
// already voted this week) it shows the results bar.
const MarketVoteCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await marketSentiment();
        if (!cancelled && res?.data?.data) setData(res.data.data);
      } catch (err) {
        console.error("market sentiment fetch failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleVote = async (type) => {
    if (voting || data?.hasVoted) return;
    const voteValue = type === "BULL" ? "1" : "2";
    setVoting(true);
    try {
      const res = await voteMarket(voteValue);
      if (res?.status === 200) {
        setData((prev) => ({
          ...prev,
          ...res.data.data,
          bull: res.data.data.bull,
          bear: res.data.data.bear,
          hasVoted: res.data.data.hasVoted,
        }));
        toast.success(res.data.message);
      }
    } catch (err) {
      console.error("vote failed", err);
      toast.error("We could not record your vote.");
    } finally {
      setVoting(false);
    }
  };

  if (loading || !data) return null;

  const bull = Number(data?.bull ?? 0);
  const bear = Number(data?.bear ?? 0);
  const hasVoted = !!data?.hasVoted;

  // Optional fields — only rendered if the backend actually returns them.
  const totalVotes =
    data?.total_votes ?? data?.totalVotes ?? data?.total ?? null;
  const endDate =
    data?.end_date ?? data?.ends_at ?? data?.voting_ends_at ?? data?.voting_end ?? null;
  const daysLeft =
    endDate != null
      ? Math.max(0, Math.ceil((new Date(endDate) - new Date()) / 86400000))
      : null;

  const metaParts = [];
  if (totalVotes != null)
    metaParts.push(`${Number(totalVotes).toLocaleString()} total votes`);
  if (daysLeft != null)
    metaParts.push(daysLeft === 0 ? "Ends today" : `Ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`);

  return (
    <div className="w-full bg-white border-b border-[#D7DADF] sm:border-b-0 mb-5">
      <div className="feed-shadow px-[18px] py-4 sm:px-5 sm:py-5 sm:rounded-2xl sm:bg-white">
        {!hasVoted ? (
          <>
            <h3 className="text-center text-base sm:text-lg font-bold text-gray-900">
              Weekly Market Vote
            </h3>
            <p className="text-center text-sm text-gray-500 mt-1">
              Are you bullish or bearish this week?
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                onClick={() => handleVote("BULL")}
                disabled={voting}
                className="flex items-center justify-center gap-2 border border-gray-200 rounded-xl py-2 transition-colors disabled:opacity-60 md:hover:border-[#35b23c]"
              >
                <img src={BULL_IMG} alt="Bull" className="w-12 h-12 object-contain flex-shrink-0" />
                <span className="font-semibold text-green-600">Bullish</span>
              </button>
              <button
                type="button"
                onClick={() => handleVote("BEAR")}
                disabled={voting}
                className="flex items-center justify-center gap-2 border border-gray-200 rounded-xl py-2 transition-colors disabled:opacity-60 md:hover:border-red-500"
              >
                <img src={BEAR_IMG} alt="Bear" className="w-12 h-12 object-contain flex-shrink-0" />
                <span className="font-semibold text-red-500">Bearish</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-center text-base sm:text-lg font-bold text-gray-900">
              The crowd has spoken
            </h3>
            <div className="flex items-center gap-2 mt-4">
              <span className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center">
                <img src={BULL_IMG} alt="Bull" className="w-10 h-10 object-contain" />
              </span>
              <div className="flex-1 h-4 rounded-full overflow-hidden bg-red-500">
                <div
                  className="h-full bg-green-500"
                  style={{ width: `${Math.min(100, Math.max(0, bull))}%` }}
                />
              </div>
              <span className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center">
                <img src={BEAR_IMG} alt="Bear" className="w-10 h-10 object-contain" />
              </span>
            </div>
            <div className="flex justify-between mt-2 text-sm font-semibold">
              <span className="text-green-600">{bull}% Bullish</span>
              <span className="text-red-500">{bear}% Bearish</span>
            </div>
            {metaParts.length > 0 && (
              <p className="text-left text-xs text-gray-400 mt-2">
                {metaParts.join(" • ")}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarketVoteCard;
